<template>
  <!-- One call is its own row: wrapping it in a group would add a click and say nothing the
       row does not already say. -->
  <agent-message v-if="messages.length === 1" :message="messages[0]" />

  <div v-else class="agent-tool-group">
    <button
      type="button"
      class="agent-tool-group__summary"
      :class="{ 'agent-tool-group__summary--open': expanded }"
      :aria-expanded="expanded"
      :disabled="hasPending"
      @click="expanded = !expanded"
    >
      <span class="agent-tool-group__caret" :class="{ 'agent-tool-group__caret--open': expanded }" v-html="caretIcon" />
      <span class="agent-tool-group__count">{{ messages.length }} steps</span>
      <span class="agent-tool-group__names">{{ names }}</span>
      <span v-if="errors" class="agent-tool-group__errors">{{ errors }} failed</span>
    </button>

    <div v-if="expanded" class="agent-tool-group__rows">
      <agent-message v-for="message in messages" :key="message.id" :message="message" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue';
import { AgentApprovalState, type AgentMessageDto } from '@/ApiClient';
import { toolLabelSummary } from '@lib/agent-tools';
import AgentMessage from './agent-message.vue';
import caretIcon from '@icons/other/caret-down.svg';

const { messages } = defineProps<{ messages: AgentMessageDto[] }>();

// A call waiting on the user cannot be hidden behind a click — the card holding Allow and Deny
// is inside it, and a collapsed group would look like the agent had simply stopped.
const hasPending = computed(() => messages.some((m) => m.approvalState === AgentApprovalState.Pending));

const expanded = ref(false);
watchEffect(() => {
  if (hasPending.value) expanded.value = true;
});

const errors = computed(() => messages.filter((m) => m.toolIsError).length);

const names = computed(() => toolLabelSummary(messages));
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.agent-tool-group {
  width: 100%;

  &__summary {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    width: 100%;
    padding: 0.8rem 1.2rem;
    border: 1px solid $c-hairline;
    border-radius: $br-medium-heavy;
    background: $c-surface-muted;
    cursor: pointer;
    font: inherit;
    font-size: 1.2rem;
    text-align: left;
    color: $c-ink-soft;
    transition: background $transition-fast ease;

    &:hover:not(:disabled) {
      background: $c-surface;
      color: $c-ink;
    }

    &:disabled {
      cursor: default;
    }

    &--open {
      border-radius: $br-medium-heavy $br-medium-heavy 0 0;
      border-bottom-color: transparent;
    }
  }

  &__caret {
    display: flex;
    flex: none;

    :deep(svg) {
      width: 0.9rem;
      height: 0.9rem;
      fill: currentColor;
      transition: transform $transition-fast ease;
      transform: rotate(-90deg);
    }

    &--open :deep(svg) {
      transform: none;
    }
  }

  &__count {
    flex: none;
    font-weight: 600;
    color: $c-ink;
  }

  &__names {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__errors {
    flex: none;
    margin-left: auto;
    color: $c-red;
  }

  &__rows {
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    padding: 0.8rem;
    border: 1px solid $c-hairline;
    border-top: none;
    border-radius: 0 0 $br-medium-heavy $br-medium-heavy;
  }
}
</style>
